import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common/PageHeader";
import { PermissionMatrix } from "@/components/common/PermissionMatrix";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Lock, ShieldCheck } from "lucide-react";

export default function Roles() {
  const { can } = useAuth();
  const isSuper = can("*");
  const [meta, setMeta] = useState({ roles: [], modules: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role object or {new:true}
  const [label, setLabel] = useState("");
  const [perms, setPerms] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roles");
      setMeta(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (role) => {
    setEditing(role);
    setLabel(role?.label || "");
    setPerms(new Set(role?.permissions || []));
  };
  const openNew = () => { setEditing({ new: true, system: false }); setLabel(""); setPerms(new Set()); };
  const close = () => { setEditing(null); setLabel(""); setPerms(new Set()); };

  const toggle = (perm, next) => {
    setPerms((prev) => {
      const s = new Set(prev);
      if (next) s.add(perm); else s.delete(perm);
      return s;
    });
  };

  const save = async () => {
    if (!label.trim()) { toast.error("Role name is required"); return; }
    setSaving(true);
    try {
      const payload = { label: label.trim(), permissions: [...perms] };
      if (editing.new) await api.post("/roles", payload);
      else await api.put(`/roles/${editing.key}`, payload);
      toast.success(editing.new ? "Role created" : "Role updated");
      close(); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/roles/${delTarget.key}`);
      toast.success("Role deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };

  if (loading) return <Loading label="Loading roles" />;
  const isFull = editing && (editing.permissions || []).includes("*");
  const readonly = editing && editing.system && !editing.new;

  return (
    <div className="space-y-6">
      <PageHeader title="Roles & Permissions" subtitle="Define roles and the exact modules and actions each can access.">
        {isSuper && (
          <Button data-testid="add-role-btn" onClick={openNew}><Plus className="mr-1 h-4 w-4" /> New Role</Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {meta.roles.map((r) => {
          const full = (r.permissions || []).includes("*");
          const count = full ? "All" : (r.permissions || []).length;
          return (
            <div key={r.key} data-testid={`role-card-${r.key}`} className="flex flex-col justify-between rounded-sm border border-border bg-card p-5">
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <h3 className="font-display text-base font-bold tracking-tight">{r.label}</h3>
                  </div>
                  {r.system && <span className="flex items-center gap-1 rounded-sm bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><Lock className="h-3 w-3" /> Built-in</span>}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{count === "All" ? "Unrestricted full access" : `${count} permission${count === 1 ? "" : "s"}`}</p>
                <p className="mt-1 text-xs text-muted-foreground">{r.user_count} user{r.user_count === 1 ? "" : "s"} assigned</p>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`view-role-${r.key}`}>
                  <Pencil className="mr-1 h-3.5 w-3.5" /> {r.system ? "View" : "Edit"}
                </Button>
                {isSuper && !r.system && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDelTarget(r)} data-testid={`delete-role-${r.key}`}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.new ? "New Role" : editing?.label}</DialogTitle>
            <DialogDescription>
              {readonly ? "Built-in role — permissions are fixed." : "Toggle the modules and actions this role can perform."}
            </DialogDescription>
          </DialogHeader>

          {isFull ? (
            <div className="rounded-sm border border-border bg-secondary/40 p-4 text-sm">
              <strong>Super Admin</strong> has unrestricted access to every module and action. This role is locked and cannot be modified.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="role-label" className="eyebrow">Role Name</Label>
                <Input id="role-label" data-testid="role-label-input" value={label} onChange={(e) => setLabel(e.target.value)} disabled={readonly} placeholder="e.g. Shift Supervisor" />
              </div>
              <PermissionMatrix
                modules={meta.modules}
                actions={meta.actions}
                value={perms}
                onToggle={toggle}
                disabled={readonly}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="role-cancel">Close</Button>
            {isSuper && !readonly && !isFull && (
              <Button onClick={save} disabled={saving} data-testid="role-save">{saving ? "Saving…" : "Save Role"}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title={`Delete role "${delTarget?.label}"?`}
        description="This cannot be undone. Roles with assigned users cannot be deleted."
        confirmText="Delete role"
        destructive
        onConfirm={remove}
      />
    </div>
  );
}
